/**
 * Alert Component
 *
 * A molecular component for displaying important messages to users.
 * Supports different variants, icons, and actions.
 */

import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';
export type AlertSize = 'sm' | 'md' | 'lg';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  size?: AlertSize;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  children?: React.ReactNode;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      variant = 'info',
      size = 'md',
      title,
      description,
      icon,
      action,
      dismissible = false,
      onDismiss,
      children,
      className,
      ...props
    },
    ref
  ) => {
    // Base alert classes
    const baseClasses = cn(
      'rounded-lg border p-4 transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2'
    );

    // Variant classes
    const variantClasses = {
      info: cn(
        'bg-info-50 border-info-200 text-info-800',
        'dark:bg-info-900 dark:border-info-800 dark:text-info-200',
        'focus:ring-info-500'
      ),
      success: cn(
        'bg-success-50 border-success-200 text-success-800',
        'dark:bg-success-900 dark:border-success-800 dark:text-success-200',
        'focus:ring-success-500'
      ),
      warning: cn(
        'bg-warning-50 border-warning-200 text-warning-800',
        'dark:bg-warning-900 dark:border-warning-800 dark:text-warning-200',
        'focus:ring-warning-500'
      ),
      error: cn(
        'bg-error-50 border-error-200 text-error-800',
        'dark:bg-error-900 dark:border-error-800 dark:text-error-200',
        'focus:ring-error-500'
      ),
    };

    // Size classes
    const sizeClasses = {
      sm: 'p-3 text-sm',
      md: 'p-4 text-sm',
      lg: 'p-6 text-base',
    };

    // Default icons for each variant
    const defaultIcons = {
      info: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      ),
      success: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
      warning: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
      error: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      ),
    };

    const displayIcon = icon || defaultIcons[variant];

    return (
      <div
        ref={ref}
        className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
        role="alert"
        aria-live="polite"
        {...props}
      >
        <div className="flex items-start">
          {displayIcon && <div className="flex-shrink-0 mr-3">{displayIcon}</div>}

          <div className="flex-1 min-w-0">
            {title && <h3 className="font-medium text-current mb-1">{title}</h3>}

            {description && <p className="text-current opacity-90">{description}</p>}

            {children && <div className="mt-2">{children}</div>}
          </div>

          {(action || dismissible) && (
            <div className="flex items-center ml-3">
              {action}
              {dismissible && onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="ml-2 text-current opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-2 rounded"
                  aria-label="Dismiss alert"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export { Alert };
